import { Icon, type IconType, type PolymorphicProps } from 'ht-sdk/react';
import { Avatar } from './avatar';
import type { DashboardProfile } from '.';
import type { ElementType } from 'react';

type BaseNavItemProps = {
	active?: boolean;
};

type NavItemProps = BaseNavItemProps & {
	icon: IconType;
	name: string;
};

export function NavItem<T extends ElementType = 'span'>({ icon, name, as, active, ...props }: PolymorphicProps<T, NavItemProps>) {
	const Tag = as || 'span';

	return (
		<Tag
			data-active={active ? true : undefined}
			className="dark:shadow-theme-500/70 dark:data-[active]:border-theme-500/30 group shadow-theme-600 data-[active]:border-theme-100 data-[active]:bg-theme-500 data-[active]:text-theme-50 dark:data-[active]:bg-theme-600 dark:data-[active]:text-theme-300 relative flex cursor-pointer items-center gap-2 rounded-md border-t border-transparent p-2 text-sm text-gray-800 transition-all group-data-[expanded]/nav:px-3 hover:bg-gray-100 data-[active]:shadow-sm dark:text-gray-300 dark:hover:bg-gray-700"
			onDragStart={(e) => e.preventDefault()}
			{...props}
		>
			<Icon
				icon={icon}
				className="group-data-[active]:text-theme-100 dark:group-data-[active]:text-theme-400 pointer-events-none size-5 shrink-0 p-[2px] text-gray-500 dark:text-gray-400"
			/>
			<span className="hidden w-0 shrink overflow-hidden whitespace-nowrap group-data-[expanded]/nav:block group-data-[expanded]/nav:w-auto">
				{name}
			</span>
			<span className="before:clip-half pointer-events-none absolute -right-4 translate-x-full rounded-md border border-gray-200 bg-white p-2 leading-none whitespace-nowrap text-gray-800 opacity-0 drop-shadow-sm group-hover:opacity-100 group-data-[expanded]/nav:hidden before:absolute before:top-1/2 before:left-0 before:block before:size-2 before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:rounded-sm before:border before:border-gray-200 before:bg-inherit dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:before:border-gray-700 dark:before:bg-gray-950">
				{name}
			</span>
		</Tag>
	);
}

type ProfileNavItemProps = BaseNavItemProps & {
	profile: DashboardProfile;
	userIcon: IconType;
};

export const ProfileNavItem = <T extends ElementType = 'span'>({
	as,
	profile,
	active,
	userIcon,
	...props
}: PolymorphicProps<T, ProfileNavItemProps>) => {
	const Tag = as || 'span';
	return (
		<Tag
			data-active={active ? true : undefined}
			onDragStart={(e) => e.preventDefault()}
			className="dark:shadow-theme-500/70 dark:data-[active]:border-theme-500/30 group shadow-theme-600 data-[active]:border-theme-100 data-[active]:bg-theme-500 data-[active]:text-theme-50 dark:data-[active]:bg-theme-600 dark:data-[active]:text-theme-300 relative flex items-center gap-2 rounded-md border-t border-transparent p-2 text-sm text-gray-800 transition-all group-data-[expanded]/nav:px-3 hover:bg-gray-100 data-[active]:shadow-sm dark:text-gray-300 dark:hover:bg-gray-700"
			{...props}
		>
			<Avatar profile={profile} userIcon={userIcon} />
			<span className="hidden w-0 max-w-[10ch] shrink overflow-hidden text-ellipsis whitespace-nowrap group-data-[expanded]/nav:block group-data-[expanded]/nav:w-auto">
				{profile.name}
			</span>
		</Tag>
	);
};
